import { useState, type ReactNode } from 'react';
import { useEscapeLayer } from './Modal';
import { BTN_DANGER, BTN_PRIMARY, BTN_SECONDARY } from './ui';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

/**
 * Confirmation modal shared by both dashboards. Uses semantic Tailwind tokens
 * (bg-card, text-foreground, bg-destructive, overlay-backdrop) driven by each
 * app's CSS variables, so it renders in each app's theme.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [loading, setLoading] = useState(false);
  // Escape cancels THIS dialog — not the modal it was opened over.
  useEscapeLayer(open, () => { if (!loading) onCancel(); });

  if (!open) return null;


  async function handleConfirm() {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  }

  return (
    // The Modal shell's look (backdrop, panel, pop-in) and the app's button styles: Cancel is the
    // secondary button, the confirm is the danger or primary one. A backdrop press cancels.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overlay-backdrop p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onCancel(); }}
    >
      <div role="alertdialog" aria-modal="true" aria-label={title}
        className="w-full max-w-md animate-modal-pop rounded-xl border border-border bg-card p-6 shadow-modal">
        <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
        {/* A div, not a <p>: callers pass lists, an error line or other blocks as the message. */}
        <div className="mt-2 text-[13px] text-muted-foreground">{message}</div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={loading} className={BTN_SECONDARY}>
            {cancelLabel}
          </button>
          <button type="button" onClick={handleConfirm} disabled={loading} className={variant === 'danger' ? BTN_DANGER : BTN_PRIMARY}>
            {loading ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
